/**
 * The installation's ordering parameters.
 *
 * Everything on this screen is a number a distributor negotiates and changes:
 * the VAT rate, what shipping costs, the threshold above which it stops
 * costing anything, whether a credit overrun holds an order, the payment
 * terms, the two reference prefixes and how long a quotation stands. A literal
 * in the source would make the second installation a fork rather than a
 * deployment, which is §1 of the cadrage.
 *
 * The credit switch's label says what "held" means, because the word is
 * ambiguous and the wrong reading is the dangerous one: it means *awaiting an
 * ADV decision*, never *refused*.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import {
  Button,
  Card,
  Checkbox,
  ErrorState,
  Input,
  LoadingState,
  Page,
  useToast,
} from "@hardauto/core-ui";

import { OrdersApi, type OrderPolicy } from "../api";
import { NAMESPACE } from "../i18n";
import "./orders.css";

export function PolicyScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const api = useMemo(() => new OrdersApi(client), [client]);
  const mayWrite = useCan("orders.policy.write");

  const fetcher = useCallback(() => api.policy(), [api]);
  const policy = useResource(fetcher);

  const [draft, setDraft] = useState<OrderPolicy | null>(null);

  useEffect(() => {
    if (policy.data && draft === null) setDraft(policy.data);
  }, [policy.data, draft]);

  if (policy.loading && !policy.data) {
    return (
      <Page title={t("policy.title")}>
        <LoadingState label={t("policy.loading")} />
      </Page>
    );
  }
  if (!draft) {
    return (
      <Page title={t("policy.title")}>
        <ErrorState
          title={t("error.title")}
          onRetry={policy.reload}
          retryLabel={t("error.retry")}
        />
      </Page>
    );
  }

  return (
    <Page title={t("policy.title")} description={t("policy.description")}>
      <Card>
        <form
          className="hardauto-stack"
          onSubmit={(event) => {
            event.preventDefault();
            void action.run("save", async () => {
              try {
                const saved = await api.savePolicy(draft);
                setDraft(saved);
                toast.show(t("policy.saved"), { tone: "success" });
              } catch {
                toast.show(t("error.title"), { tone: "danger" });
              }
            });
          }}
        >
          <Field label={t("policy.taxRate")} required>
            {(props) => (
              <Input
                {...props}
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={draft.tax_rate_pct}
                disabled={!mayWrite}
                onChange={(event) => setDraft({ ...draft, tax_rate_pct: event.target.value })}
              />
            )}
          </Field>

          <Field label={t("policy.shippingFlat")}>
            {(props) => (
              <Input
                {...props}
                type="number"
                min="0"
                step="0.01"
                value={draft.shipping_flat}
                disabled={!mayWrite}
                onChange={(event) => setDraft({ ...draft, shipping_flat: event.target.value })}
              />
            )}
          </Field>

          <Field label={t("policy.freeShippingAbove")} hint={t("policy.freeShippingHint")}>
            {(props) => (
              <Input
                {...props}
                type="number"
                min="0"
                step="0.01"
                value={draft.free_shipping_above ?? ""}
                disabled={!mayWrite}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    // An empty field is "never free", which is `null` and not
                    // zero — zero would make every order free.
                    free_shipping_above: event.target.value === "" ? null : event.target.value,
                  })
                }
              />
            )}
          </Field>

          <Checkbox
            label={t("policy.holdOverCredit")}
            checked={draft.hold_over_credit_limit}
            disabled={!mayWrite}
            onChange={(event) =>
              setDraft({ ...draft, hold_over_credit_limit: event.target.checked })
            }
          />
          <p className="hardauto-cell__muted">{t("policy.holdOverCreditHint")}</p>

          <Field label={t("policy.paymentTerms")} hint={t("policy.paymentTermsHint")}>
            {(props) => (
              <Input
                {...props}
                value={draft.payment_terms}
                disabled={!mayWrite}
                onChange={(event) => setDraft({ ...draft, payment_terms: event.target.value })}
              />
            )}
          </Field>

          <Field label={t("policy.referencePrefix")} hint={t("policy.referencePrefixHint")}>
            {(props) => (
              <Input
                {...props}
                maxLength={8}
                value={draft.reference_prefix}
                disabled={!mayWrite}
                onChange={(event) =>
                  setDraft({ ...draft, reference_prefix: event.target.value.toUpperCase() })
                }
              />
            )}
          </Field>

          <Field
            label={t("policy.quoteReferencePrefix")}
            hint={t("policy.quoteReferencePrefixHint")}
          >
            {(props) => (
              <Input
                {...props}
                maxLength={8}
                value={draft.quote_reference_prefix}
                disabled={!mayWrite}
                onChange={(event) =>
                  setDraft({ ...draft, quote_reference_prefix: event.target.value.toUpperCase() })
                }
              />
            )}
          </Field>

          <Field
            label={t("policy.quoteValidityDays")}
            hint={t("policy.quoteValidityDaysHint")}
            required
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min="1"
                max="365"
                step="1"
                value={draft.quote_validity_days}
                disabled={!mayWrite}
                onChange={(event) =>
                  setDraft({ ...draft, quote_validity_days: Number(event.target.value) })
                }
              />
            )}
          </Field>

          {mayWrite ? (
            <Button
              type="submit"
              variant="primary"
              busy={action.isBusy("save")}
              busyLabel={t("policy.saving")}
            >
              {t("policy.save")}
            </Button>
          ) : null}
        </form>
      </Card>
    </Page>
  );
}
